import { LEAD_STATES, leadStateText, type Term, type Tone } from "@/lib/campaign";
import { DROPPED_OUTCOME } from "@/lib/call-back";
import { DEFAULT_CALLBACK_SECONDS, DEFAULT_HOLD_SECONDS, DROPPED_WAIT_SECONDS } from "@/lib/dispositions";
import { humanize } from "@/lib/lead-card";
import { catalogRow, groupText, stageCatalog, stageSection, type StageSection } from "@/lib/stages";
import { SCHEDULED_WHY, scheduledFor, sentStatusText, workflowSender, type TextTone } from "@/lib/texts";
import type {
  ContactRow, ContactSummary, Disposition, LeadCard, LeadNote, LeadOwnerChange, LeadStageChange, LeadTimelineEvent,
  TextMessage,
} from "@/lib/types";
import { formatDateTime } from "@/lib/utils";
import { waitingFor } from "@/lib/waiting-for";

/**
 * The contacts list and the record (ADR-044 in `platform`), in words: the stages and their three
 * groups (ADR-046), the Dial column's vocabulary on top of the dialer's own, the built-in views as
 * filter sets, the URL as the one place a filter lives, and what a timeline entry or an outcome
 * says. Pure, for `test/contacts.test.ts`.
 *
 * The stages are the tenant's own catalog since ADR-077 (`lib/stages.ts`): every helper here reads
 * it first, and the built-in thirteen below answer until it has loaded, or for a name it no longer
 * has (a deleted stage stays in old history rows).
 *
 * Two facts sit side by side on every row and are never merged: the **stage** is the person's, moved
 * by what happened (`lifecycle.py`); the **dial state** is one campaign's list row, the dialer's.
 */

// --- the stages ------------------------------------------------------------------------------------

export const WORKING_STAGES = [
  "new", "no_response", "chase", "callback_missed", "consult_no_show", "about_to_sign",
] as const;
export const SCHEDULED_STAGES = ["callback_scheduled", "consult_scheduled"] as const;
export const EXIT_STAGES = ["signed_pending_intake", "signed", "not_qualified", "dnc", "mia"] as const;
export const STAGES = [...WORKING_STAGES, ...SCHEDULED_STAGES, ...EXIT_STAGES] as const;
export type Stage = (typeof STAGES)[number];
export type StageGroup = "working" | "scheduled" | "exit";

const STAGE: Record<Stage, Term> = {
  new: { label: "New", description: "Arrived; no outcome yet." },
  no_response: { label: "No response", description: "Called, not reached. The dialer keeps trying." },
  chase: { label: "Chase", description: "Reached and interested; being chased for the next step." },
  callback_missed: { label: "Callback missed", description: "A promised callback that did not connect." },
  consult_no_show: { label: "Consult no-show", description: "Missed the contract review." },
  about_to_sign: { label: "About to sign", description: "The contract is out; waiting for the signature." },
  callback_scheduled: { label: "Callback scheduled", description: "A callback is booked. Not dialed before it." },
  consult_scheduled: { label: "Consult scheduled", description: "A contract review is booked. Not dialed before it." },
  signed_pending_intake: { label: "Signed, pending intake", description: "Signed; the intake is not finished." },
  signed: { label: "Signed", description: "A client. Never dialed again, for any firm." },
  not_qualified: { label: "Not qualified", description: "Not a case for this firm. Off its lists." },
  dnc: { label: "Do not call", description: "Asked not to be called. Every record with the phone." },
  mia: { label: "MIA", description: "The firm could not reach its client. Called again on an MIA cadence, and on no other." },
};

const GROUP_TONE: Record<StageGroup, Tone> = { working: "accent", scheduled: "warning", exit: "neutral" };

function builtInGroup(stage: string | null): StageGroup | null {
  if ((WORKING_STAGES as readonly string[]).includes(stage ?? "")) return "working";
  if ((SCHEDULED_STAGES as readonly string[]).includes(stage ?? "")) return "scheduled";
  if ((EXIT_STAGES as readonly string[]).includes(stage ?? "")) return "exit";
  return null;
}

export function stageGroup(stage: string | null): StageGroup | null {
  return catalogRow(stage)?.group ?? builtInGroup(stage);
}

/** The heading a stage is listed under in a picker (`stageSection` in `lib/stages.ts`): its group,
 *  but Recovery for MIA. */
export function stageSectionOf(stage: string | null): StageSection | null {
  const group = stageGroup(stage);
  return stage && group ? stageSection(stage, group) : null;
}

/** A stage in a rep's words. A lead older than the lifecycle has none; a stage this console does not
 *  know is shown as itself. */
export function stageText(stage: string | null): Term & { tone: Tone } {
  if (!stage) {
    return { label: "No stage", description: "Older than the lifecycle: no outcome has moved it yet.", tone: "outline" };
  }
  const tone = (group: StageGroup): Tone => (stage === "dnc" ? "danger" : GROUP_TONE[group]);
  const row = catalogRow(stage);
  if (row) {
    return { label: row.label, description: row.description || groupText(row.group).description, tone: tone(row.group) };
  }
  const known = STAGE[stage as Stage];
  const group = builtInGroup(stage);
  if (!known || !group) return { label: humanize(stage) ?? stage, description: "Not a stage this console knows.", tone: "outline" };
  return { ...known, tone: tone(group) };
}

/** Every stage, in the order the console lists them: the tenant's catalog, or the built-in thirteen. */
export function stageSlugs(): string[] {
  return stageCatalog()?.map((s) => s.slug) ?? [...STAGES];
}

/** Whether `stage` is one of the tenant's stages now: a picker keeps a value it no longer offers. */
export function isCurrentStage(stage: string | null | undefined): boolean {
  return Boolean(stage) && stageSlugs().includes(stage!);
}

/** An exit's reach (rule 3 of ADR-046): every record with the phone, or only the same firm's.
 *  Null for a stage that is not an exit. */
export function exitReach(stage: string | null): "all_firms" | "same_firm" | null {
  const row = catalogRow(stage);
  if (row) return row.group === "exit" ? row.exit_scope ?? "same_firm" : null;
  if (stage === "not_qualified" || stage === "mia") return "same_firm";
  return builtInGroup(stage) === "exit" ? "all_firms" : null;
}

/** Where an admin may reopen a lead to (ADR-076): any working or scheduled stage, never an exit. */
export const REOPEN_STAGES: readonly Stage[] = [...WORKING_STAGES, ...SCHEDULED_STAGES];

/** The same, from the tenant's catalog once it has loaded. */
export function reopenStages(): string[] {
  const catalog = stageCatalog();
  return catalog ? catalog.filter((s) => s.group !== "exit").map((s) => s.slug) : [...REOPEN_STAGES];
}

/** What a reopen did, in a sentence for the toast. */
export function reopenSummary(result: { stage: string; reopened: string[]; lists_restored: number }): string {
  const lists = result.lists_restored === 0
    ? "It was on no dial list the exit had taken it off."
    : `Back on ${result.lists_restored} dial list${result.lists_restored === 1 ? "" : "s"}, where each left off.`;
  const others = result.reopened.length - 1;
  const siblings = others > 0
    ? ` ${others} contact${others === 1 ? "" : "s"} with the same phone came back with it.`
    : "";
  return `Now ${stageText(result.stage).label}. ${lists}${siblings}`;
}

/** The tiles across the top of the list: the working stages and the win, each with its "due now". */
export const STRIP_STAGES: readonly Stage[] = [...WORKING_STAGES, "signed_pending_intake"];

/** The same, from the tenant's catalog once it has loaded: its working stages, then the win. */
export function stripStages(): string[] {
  const catalog = stageCatalog();
  if (!catalog) return [...STRIP_STAGES];
  return [...catalog.filter((s) => s.group === "working").map((s) => s.slug), "signed_pending_intake"];
}

// --- the Dial column ---------------------------------------------------------------------------------

const DIAL_EXTRA: Record<string, Term & { tone: Tone }> = {
  held: {
    label: "Held",
    description: "Kept off the list until the hold passes: a booking, a hold outcome, or the firm paused.",
    tone: "warning",
  },
  on_call: { label: "On a call", description: "Offered to a rep, or a call to the lead is up.", tone: "accent" },
  // A call that is over while the dialer still holds the lead for the rep's outcome (ACL-486).
  needs_outcome: {
    label: "Waiting for an outcome",
    description: "The call is over and the rep has not said what happened. The dialer moves on once an outcome is recorded.",
    tone: "warning",
  },
  do_not_call: { label: "Do not call", description: "Nothing dials a do-not-call lead.", tone: "danger" },
  sms_only: {
    label: "SMS only",
    description: "Asked to be texted, not called. The dialer leaves them where they are until they are set back to calls and texts.",
    tone: "warning",
  },
  text_first: {
    label: "Text first",
    description: "Texted first: the dialer leaves them where they are until the 24 hours are over, then calls as before.",
    tone: "warning",
  },
};

/** What the Dial column says: core-api's word when it has one, else the dialer's own state.
 *  `outcomeRep` is the membership's `outcome_rep`: a row waiting for an outcome names whose. */
export function dialText(dial: string | null, outcomeRep?: string | null): Term & { tone: Tone } {
  if (!dial) return { label: "Not on a list", description: "On no dial list.", tone: "outline" };
  if (dial === "needs_outcome" && outcomeRep) return { ...DIAL_EXTRA.needs_outcome!, label: waitingFor(outcomeRep) };
  return DIAL_EXTRA[dial] ?? leadStateText(dial);
}

/** What the dial state filters offer: the dialer's states, with "waiting for an outcome" in
 *  place of its parked state, because core-api's `needs_outcome` also finds a call that is over
 *  while the dialer still says `in_flight` (ACL-486). A value chosen before, such as a saved
 *  list's `needs_disposition`, stays offered so the filter keeps showing it. */
export function dialStateChoices(current?: string | null): { value: string; label: string }[] {
  const choices = LEAD_STATES.map((s): { value: string; label: string } =>
    s === "needs_disposition"
      ? { value: "needs_outcome", label: DIAL_EXTRA.needs_outcome!.label }
      : { value: s, label: leadStateText(s).label });
  if (current && !choices.some((c) => c.value === current)) {
    choices.push({ value: current, label: leadStateText(current).label });
  }
  return choices;
}

/** "Due in 1 min · Cycle 1, call 2 next" after "Next:" — each part starts lowercase, and a date
 *  or a name inside a part keeps its case: "waiting for Rep Two's outcome", never "rep two's". */
export function inSentence(line: string): string {
  return line
    .split(" · ")
    .map((part) => part.charAt(0).toLowerCase() + part.slice(1))
    .join(" · ");
}

const COUNTS_ATTEMPTS: ReadonlySet<string> = new Set([
  "fresh", "retrying", "held", "on_call", "needs_outcome", "needs_disposition",
]);

const HOLD_REASON: Record<string, string> = {
  "firm:paused": "firm paused",
  "firm:resumed": "firm resumed",
};

/** "4 of 14" under a state that is still counting, or why a row is held. */
export function dialDetail(row: Pick<ContactRow, "dial" | "membership">): string | null {
  const m = row.membership;
  if (!m) return null;
  if (row.dial === "held" && m.hold_reason) {
    return HOLD_REASON[m.hold_reason] ?? humanize(m.hold_reason) ?? m.hold_reason;
  }
  if (row.dial && COUNTS_ATTEMPTS.has(row.dial) && m.max_attempts) return `${m.attempts} of ${m.max_attempts}`;
  return null;
}

/** "in 25 min", "in 4 h", "in 2 d 3 h" — or the date, once it is more than a week off. */
export function dueIn(iso: string, now: number = Date.now()): string {
  const seconds = Math.round((Date.parse(iso) - now) / 1000);
  if (!Number.isFinite(seconds) || seconds <= 0) return "now";
  if (seconds > 7 * 86_400) return `on ${formatDateTime(iso)}`;
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (days > 0) return hours > 0 ? `in ${days} d ${hours} h` : `in ${days} d`;
  if (hours > 0) return minutes > 0 && minutes < 60 ? `in ${hours} h ${minutes} min` : `in ${hours} h`;
  return `in ${Math.max(1, minutes)} min`;
}

/** The Next column: when the dialer takes the row again, in words; null when it never will. */
export function nextText(row: Pick<ContactRow, "dial" | "next_at">, now: number = Date.now()): string | null {
  if (row.dial === "held") return row.next_at ? `Held until ${formatDateTime(row.next_at)}` : "Held";
  if (row.dial === "fresh" || row.dial === "retrying") {
    return row.next_at ? `Due ${dueIn(row.next_at, now)}` : "Due now";
  }
  // When Text first's 24 hours are over and the dialer may call again (ACL-344).
  if (row.dial === "text_first") return row.next_at ? `Due ${dueIn(row.next_at, now)}` : null;
  return null;
}

// --- the filters, and the URL they live in ----------------------------------------------------------

export interface ContactFilters {
  q?: string;
  /** Firm codes, comma-separated; `none` for no firm. */
  firm?: string;
  stage?: string;
  stage_group?: string;
  language?: string;
  state?: string;
  dial_state?: string;
  dnc?: boolean;
  member?: boolean;
  source?: string;
  /** RFC 3339, or `today` — resolved to local midnight when the request is made, so a link stays "today". */
  created_from?: string;
  created_to?: string;
  sort?: string;
  order?: string;
}

export const FILTER_KEYS = [
  "q", "firm", "stage", "stage_group", "language", "state", "dial_state", "dnc", "member", "source",
  "created_from", "created_to", "sort", "order",
] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
const BOOLEAN_KEYS: ReadonlySet<FilterKey> = new Set(["dnc", "member"]);

export const DEFAULT_SORT = "created";
export const DEFAULT_ORDER = "desc";
export const SORTS: Record<string, string> = {
  created: "Created",
  name: "Name",
  stage: "Stage",
  phone: "Phone",
};

export function filtersFromParams(params: URLSearchParams): ContactFilters {
  const out: Record<string, string | boolean> = {};
  for (const key of FILTER_KEYS) {
    const raw = params.get(key);
    if (raw === null || raw === "") continue;
    if (BOOLEAN_KEYS.has(key)) {
      if (raw === "true" || raw === "false") out[key] = raw === "true";
    } else {
      out[key] = raw;
    }
  }
  return out as ContactFilters;
}

export function paramsFromFilters(filters: ContactFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value === undefined || value === "") continue;
    if (key === "sort" && value === DEFAULT_SORT) continue;
    if (key === "order" && value === DEFAULT_ORDER) continue;
    params.set(key, String(value));
  }
  return params;
}

const NARROWING: readonly FilterKey[] = FILTER_KEYS.filter((k) => k !== "sort" && k !== "order");

/** Whether anything narrows the list. Sorting does not. */
export function isFiltered(filters: ContactFilters): boolean {
  return NARROWING.some((key) => filters[key] !== undefined && filters[key] !== "");
}

/** Local midnight, as RFC 3339: "today" as a floor manager means it, not "24 hours ago". */
export function localMidnight(now: Date = new Date()): string {
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  return midnight.toISOString();
}

/** The first of this month at local midnight, as RFC 3339: "this month" on the viewer's calendar. */
export function localMonthStart(now: Date = new Date()): string {
  const first = new Date(now);
  first.setDate(1);
  first.setHours(0, 0, 0, 0);
  return first.toISOString();
}

/** The filters as the API takes them: `today` resolved, the defaults left out. */
export function apiFilters(filters: ContactFilters, now: Date = new Date()): Record<string, string | boolean | undefined> {
  const out: Record<string, string | boolean | undefined> = { ...filters };
  if (out.created_from === "today") out.created_from = localMidnight(now);
  if (out.sort === DEFAULT_SORT) delete out.sort;
  if (out.order === DEFAULT_ORDER) delete out.order;
  return out;
}

/** What `/leads/summary` takes: the list's filters, and the viewer's own midnight and first of the
 *  month (ACL-537). The server's day is UTC's, which in Chicago starts at 7 pm the evening before;
 *  sent from the same `now` as the list's `today`, New today counts exactly the rows its click
 *  shows, and Signed this month turns over on the viewer's calendar. */
export function summaryFilters(
  sent: Record<string, string | boolean | undefined>,
  now: Date = new Date(),
): Record<string, string | boolean | undefined> {
  return { ...sent, day_start: localMidnight(now), month_start: localMonthStart(now) };
}

// --- the built-in views -----------------------------------------------------------------------------

export interface ContactView {
  key: string;
  label: string;
  filters: ContactFilters;
  count: (summary: ContactSummary) => number;
}

const groupCount = (summary: ContactSummary, group: StageGroup) =>
  summary.by_stage.filter((s) => stageGroup(s.stage) === group).reduce((n, s) => n + s.count, 0);

/** The chips under the toolbar. Each is a filter set, so each is a link. */
export const VIEWS: readonly ContactView[] = [
  { key: "all", label: "All", filters: {}, count: (s) => s.total },
  { key: "new_today", label: "New today", filters: { created_from: "today" }, count: (s) => s.new_today },
  { key: "working", label: "Working", filters: { stage_group: "working" }, count: (s) => groupCount(s, "working") },
  { key: "booked", label: "Booked", filters: { stage_group: "scheduled" }, count: (s) => groupCount(s, "scheduled") },
  { key: "exits", label: "Exits", filters: { stage_group: "exit" }, count: (s) => groupCount(s, "exit") },
  { key: "needs_firm", label: "Needs firm", filters: { firm: "none" }, count: (s) => s.needs_firm },
  { key: "unassigned", label: "Unassigned", filters: { member: false }, count: (s) => s.unassigned },
  { key: "exhausted", label: "Exhausted", filters: { dial_state: "exhausted" }, count: (s) => s.exhausted },
  { key: "dnc", label: "Do not call", filters: { dnc: true }, count: (s) => s.dnc },
];

/** The keys a view sets. A view is lit when these match it exactly; the search, the sort and the
 *  other selects do not unlight one. */
const VIEW_KEYS: readonly FilterKey[] = ["stage_group", "stage", "firm", "member", "dial_state", "dnc", "created_from"];

export function activeView(filters: ContactFilters): string | null {
  const view = VIEWS.find((v) => VIEW_KEYS.every((key) => filters[key] === v.filters[key]));
  return view?.key ?? null;
}

/** The filters with this view applied: its keys replaced, everything else kept. */
export function applyView(filters: ContactFilters, view: ContactView): ContactFilters {
  const out: ContactFilters = { ...filters };
  for (const key of VIEW_KEYS) delete out[key];
  return { ...out, ...view.filters };
}

/** The filters with one stage tile lit — or none, clicking the lit one. */
export function toggleStage(filters: ContactFilters, stage: string): ContactFilters {
  const out: ContactFilters = { ...filters };
  delete out.stage_group;
  if (out.stage === stage) delete out.stage;
  else out.stage = stage;
  return out;
}

// --- the record --------------------------------------------------------------------------------------

export function contactName(lead: { first_name: string | null; last_name: string | null }): string | null {
  return [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() || null;
}

/** "Since 20 min ago" is `relativeTime`; this is the sentence under the header. */
export function stageLine(card: Pick<LeadCard, "stage" | "stage_since">, since: string): string {
  const text = stageText(card.stage).label;
  return card.stage_since ? `${text} · since ${since}` : text;
}

const REASON_WORDS: [RegExp, string][] = [
  [/^created$/, "when the record was created"],
  [/^disposition:/, "from the outcome"],
  [/^trigger:/, "by a trigger"],
  [/^sibling:/, "from the other record with this phone"],
  [/^arrival:dnc_sibling$/, "a record with this phone is do-not-call"],
  [/^import:/, "by the import"],
  [/^console:/, "typed into the console"],
  [/^reopen/, "reopened by an admin"],
  [/^stage_removed:/, "its stage was deleted"],
  [/^text:stop$/, "they texted STOP"],
  [/^number_refused$/, "this number asked not to be called"],
];

/** A stage move: "No response → Callback missed", and why. */
export function stageChangeText(change: LeadStageChange): { title: string; detail: string | null } {
  const to = stageText(change.to_stage).label;
  const title = change.from_stage ? `${stageText(change.from_stage).label} → ${to}` : to;
  const detail = REASON_WORDS.find(([re]) => re.test(change.reason))?.[1] ?? null;
  return { title, detail };
}

/** A change of a lead's owner on the timeline (ACL-408): "Given to Maria", "Moved from Maria to
 *  Ana", "No owner now". A person since deleted reads as "somebody". */
export function ownerChangeText(change: LeadOwnerChange): string {
  const to = change.to_name ?? (change.to_user_id !== null ? "somebody" : null);
  const from = change.from_name ?? (change.from_user_id !== null ? "somebody" : null);
  if (!to) return from ? `No owner now (was ${from})` : "No owner now";
  // Given as the lead arrived: to a rep Ready on its campaign's line, or to the rep who dialed it
  // by hand (platform ADR-184).
  const how = change.reason.startsWith("ready:")
    ? " · Ready when the lead arrived"
    : change.reason === "hand_dial" ? " · dialed it by hand" : "";
  return (from ? `Moved from ${from} to ${to}` : `Given to ${to}`) + how;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** The reasons of a `compliance.blocked` that leave the lead off the do-not-call list: the dialer's
 *  gate refused to dial (no consent evidence, or no time zone known, which the contract calls
 *  `quiet_hours`). */
const NOT_DIALED: ReadonlySet<string> = new Set(["no_consent", "quiet_hours"]);

/** Why a new lead was given to nobody (platform ADR-184), as the Slack message says it. */
const NOT_ASSIGNED: Record<string, string> = {
  no_campaign: "the lead is on no list",
  no_line: "its list has no line",
  no_rep_ready: "nobody was Ready for it",
  floor_unknown: "the reps' status could not be read",
};

/** An event-log row on the timeline, in words. Only the types core-api puts there. */
export function systemEventText(event: LeadTimelineEvent): string {
  const p = event.payload;
  switch (event.type) {
    case "lead.created":
      return `Created${text(p.source) ? ` from ${text(p.source)}` : ""}`;
    case "compliance.blocked":
      // The dialer's gate also says this for a lead it will not dial yet, and that lead is not on
      // the do-not-call list: no time zone lifts once one is set on the record (ADR-164 in platform).
      if (NOT_DIALED.has(text(p.reason) ?? "")) return `Blocked from dialing${text(p.detail) ? ` · ${text(p.detail)}` : ""}`;
      // A STOP by text says so in words (ADR-095); every other detail is shown as it is.
      if (text(p.detail) === "text:stop") return "Marked do-not-call · they texted STOP";
      return `Marked do-not-call${text(p.detail) ? ` · ${text(p.detail)}` : ""}`;
    case "disposition.set":
      // The lead's cadence ran out and set the outcome it names (ADR-155).
      if (text(p.reason) === "ran_out") return `Cadence ran out: outcome ${humanize(text(p.disposition)) ?? "set"}`;
      // The dialer called at the promised time and nobody answered (ADR-156).
      if (text(p.reason) === "missed_callback") return "Callback missed: nobody answered at the promised time";
      // The booking outcome's no-show limit was reached (ADR-181).
      if (text(p.reason) === "no_show_limit") {
        const n = typeof p.missed === "number" ? p.missed : null;
        const of = typeof p.limit === "number" ? p.limit : n;
        return `No-show limit reached${n ? ` (${n} of ${of})` : ""}: outcome ${text(p.outcome_label) ?? humanize(text(p.disposition)) ?? "set"}`;
      }
      // Nobody answered at a callback's first time, so its backup time was booked (ACL-559, ADR-183).
      if (text(p.reason) === "backup_callback") return "Backup callback booked: nobody answered at the first time";
      return `Outcome ${humanize(text(p.disposition)) ?? "set"} by ${text(p.by) ?? "the system"}`;
    case "appointment.booked":
      return "Appointment booked";
    case "appointment.no_show":
      return "Appointment missed";
    case "call.rep_connected":
      return "Direct call answered by a rep";
    case "call.missed":
      return `Missed direct call${text(p.reason) ? ` · ${humanize(text(p.reason))}` : ""}`;
    case "lead.not_assigned":
      // Nobody was given the lead as it arrived (platform ADR-184).
      return `Rep not assigned · ${NOT_ASSIGNED[text(p.reason) ?? ""] ?? humanize(text(p.reason)) ?? "no reason given"}`;
    default:
      return humanize(event.type.replaceAll(".", " ")) ?? event.type;
  }
}

/** A text on the timeline in words (ADR-095, ADR-098): which way it went, which of our numbers it
 *  was on, what came with it that the console does not show, and for one we sent, who sent it,
 *  where it stands and why it failed. A workflow's text names the workflow only to a reader who
 *  `sees` automation (ACL-591); to anyone else nobody is named. */
export function textMessageWords(
  message: Pick<TextMessage, "direction" | "from_number" | "to_number" | "media_count"> &
    Partial<Pick<TextMessage, "status" | "sent_by" | "error_detail" | "workflow" | "send_at">>,
  sees = false,
): {
  title: string;
  ourNumber: string;
  pictures: string | null;
  by: string | null;
  status: { label: string; tone: TextTone } | null;
  why: string | null;
  waits: string | null;
} {
  const inbound = message.direction === "inbound";
  const count = message.media_count;
  const status = inbound ? null : sentStatusText(message.status);
  return {
    title: inbound
      ? "Text received"
      : message.status === "failed"
        ? "Text not delivered"
        : message.status === "scheduled"
          ? "Text scheduled"
          : "Text sent",
    ourNumber: inbound ? message.to_number : message.from_number,
    pictures: count > 0 ? `${count} ${count === 1 ? "picture" : "pictures"}, not shown` : null,
    by: inbound ? null : (message.sent_by?.name ?? (sees && message.workflow ? workflowSender(message.workflow) : null)),
    status,
    why: !inbound && message.status === "failed" ? (message.error_detail ?? "The text could not be delivered.") : null,
    // Kept for the contact's 8 am (ACL-630): when it goes, said as a fact, not a failure.
    waits: !inbound && message.status === "scheduled" ? `${scheduledFor(message.send_at)}. ${SCHEDULED_WHY}` : null,
  };
}

/** What a note is: typed on the record, typed with an outcome, the handoff's brief, or the platform's.
 *  A workflow's Add note step (ACL-370) is a "Workflow note" to a reader who `sees` automation and an
 *  "Automated note" to anyone else (ACL-591). */
export function noteTitle(
  note: Pick<LeadNote, "kind" | "disposition"> & Partial<Pick<LeadNote, "workflow">>,
  label: (slug: string) => string | undefined,
  sees = false,
): string {
  if (note.workflow) return sees ? "Workflow note" : "Automated note";
  switch (note.kind) {
    case "disposition":
      return note.disposition ? `Note with ${label(note.disposition) ?? humanize(note.disposition)}` : "Note with the outcome";
    case "handoff":
      return "Handoff brief";
    case "system":
      return "System";
    default:
      return "Note";
  }
}

/** How many lines of a note the Timeline shows under its title (ACL-625): the timeline is an
 *  overview, and the Notes tab has every note whole. A handoff's brief is its title alone; the
 *  platform's own note is one line, as an event is; what a person or a workflow wrote is two. */
export function timelineNoteLines(note: Pick<LeadNote, "kind"> & Partial<Pick<LeadNote, "workflow">>): 0 | 1 | 2 {
  if (note.workflow) return 2;
  if (note.kind === "handoff") return 0;
  return note.kind === "system" ? 1 : 2;
}

// --- an outcome off a call --------------------------------------------------------------------------

/** "4 h", "30 min", "2 d": a backoff or a hold as a person says it. */
export function spanWords(seconds: number): string {
  if (seconds >= 86_400) return `${Math.round(seconds / 86_400)} d`;
  if (seconds >= 3600) return `${Math.round(seconds / 3600)} h`;
  return `${Math.max(1, Math.round(seconds / 60))} min`;
}

/** The one-line effect under an outcome's button: what choosing it does to the lead. The server
 *  decides all of it (`lifecycle.py`, the dialer's recycling); this says it before the click.
 *
 *  An outcome that ends the lead and names a campaign (MIA and the MIA campaign) leaves them on
 *  that campaign's list and no other. `handsTo` is the campaign's name, where the reader may
 *  know it: a rep cannot list campaigns. */
export function outcomeEffect(
  d: Pick<Disposition, "kind" | "category" | "backoff_seconds" | "hold_seconds" | "stage"> &
    Partial<Pick<Disposition, "enlist_campaign_id" | "slug">>,
  handsTo?: string | null,
  /** The campaign keeps working this outcome's end stage on a cadence of its own
   *  (`keepsCalling` in `lib/dispositions.ts`). */
  kept = false,
): string {
  if (d.category === "compliance") return "Every record with this phone · an admin cannot undo it";
  if (d.slug === DROPPED_OUTCOME) {
    // Kept for the rep at the same place (ADR-085): the dialer's own wait when the outcome names none.
    return `Kept for you to call back · the dialer calls in ${spanWords(d.backoff_seconds ?? DROPPED_WAIT_SECONDS)}`;
  }
  const stage = d.stage ?? null;
  const reach = exitReach(stage);
  if (reach && d.enlist_campaign_id) {
    return handsTo ? `Handed to ${handsTo} · leaves every other list` : "Moves to another list · leaves every other list";
  }
  // An end stage the campaign gives a cadence (ADR-087): the lead is not done, the calls go on.
  if (reach && kept) return `Stays on this list · called on the ${stageText(stage).label} cadence`;
  if (reach === "all_firms") return "Leaves every list · the same phone too";
  if (stage === "not_qualified" && reach) return "Asks for the reason · leaves this firm's lists";
  if (reach) return "Leaves this firm's lists";
  if (d.kind === "hold") {
    // The dialer's own default when the row names no span (`recycling.py`).
    return `Held for ${spanWords(d.hold_seconds ?? d.backoff_seconds ?? DEFAULT_HOLD_SECONDS)}`;
  }
  if (d.kind === "terminal") return stage ? `${stageText(stage).label} · done on this list` : "Done on this list";
  const back = d.backoff_seconds ? ` · due in ${spanWords(d.backoff_seconds)}` : "";
  if (d.kind === "callback") return `Callback for you · due in ${spanWords(d.backoff_seconds ?? DEFAULT_CALLBACK_SECONDS)}`;
  if (!stage) return "Label only · nothing moves";
  return `${stageText(stage).label}${back}`;
}

/** What the toast says after an outcome is saved off a call: the callback's time and its backup's
 *  (ACL-559), the stage, and the campaign the lead was handed to. */
export function outcomeSaved(
  result: { stage: string | null; stage_moved: boolean; handed_to_campaign?: string | null },
  when: string | null,
  backupWhen: string | null = null,
): string {
  const stage = stageText(result.stage).label;
  const parts = [
    when ? `Call back ${when}.` : null,
    when && backupWhen ? `If nobody answers, again ${backupWhen}.` : null,
    result.stage_moved ? `Stage: ${stage}.` : `The stage stays ${stage}.`,
    result.handed_to_campaign ? `On ${result.handed_to_campaign}'s list now.` : null,
  ];
  return parts.filter(Boolean).join(" ");
}

/** Why a working outcome is off on a lead that has left: the exit is protected (ADR-046). */
export function exitProtected(
  card: Pick<LeadCard, "stage" | "stage_since">,
  d: Pick<Disposition, "category" | "stage">,
): string | null {
  if (stageGroup(card.stage) !== "exit") return null;
  if (d.category === "compliance") return null;
  const target = d.stage ?? null;
  if (!target || stageGroup(target) === "exit") return null;
  const when = card.stage_since ? ` on ${formatDateTime(card.stage_since).split(",").slice(0, 2).join(",")}` : "";
  return `${stageText(card.stage).label}${when} — an admin can reopen`;
}
